// 🎤 Voice Search Component
import { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface VoiceSearchProps {
  onTranscript: (text: string) => void;
  placeholder?: string;
  language?: string;
  className?: string;
}

export function VoiceSearch({
  onTranscript,
  placeholder = 'Click para hablar...',
  language = 'es-ES',
  className,
}: VoiceSearchProps) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(true);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    // Verificar si el navegador soporta Web Speech API
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setIsSupported(false);
      console.warn('Web Speech API no soportada en este navegador');
      return;
    }

    // Crear instancia de reconocimiento
    const recognition = new SpeechRecognition();
    recognition.continuous = false; // Se detiene automáticamente al terminar
    recognition.interimResults = true; // Mostrar resultados intermedios
    recognition.lang = language;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      let interimText = '';
      let finalText = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalText += transcript;
        } else {
          interimText += transcript;
        }
      }


      if (finalText) {
        // Limpiar punto final que agrega automáticamente el navegador
        const cleanedText = finalText.trim().replace(/\.+$/, '');
        setTranscript(cleanedText);
        setInterimTranscript('');
        onTranscript(cleanedText);
      } else {
        setInterimTranscript(interimText);
      }
    };

    recognition.onerror = (event: any) => {
      console.error('❌ Speech recognition error:', event.error);
      setIsListening(false);

      const errorMessages: Record<string, string> = {
        'no-speech': 'No se detectó voz. Intenta de nuevo.',
        'audio-capture': 'No se puede acceder al micrófono.',
        'not-allowed': 'Permiso de micrófono denegado.',
        'network': 'Error de red. Verifica tu conexión.',
      };

      toast.error(errorMessages[event.error] || `Error: ${event.error}`);
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [language, onTranscript]);

  const toggleListening = () => {
    if (!isSupported) {
      toast.error('Tu navegador no soporta búsqueda por voz. Usa Chrome, Edge o Safari.');
      return;
    }

    if (isListening) {
      // Detener
      recognitionRef.current?.stop();
    } else {
      // Iniciar
      setTranscript('');
      setInterimTranscript('');
      recognitionRef.current?.start();
    }
  };

  if (!isSupported) {
    return null; // No mostrar el botón si no está soportado
  }

  return (
    <div className={cn('relative', className)}>
      {/* Botón de micrófono */}
      <button
        onClick={toggleListening}
        className={cn(
          'relative flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-300',
          isListening
            ? 'bg-red-500 text-white shadow-lg shadow-red-500/50 animate-pulse'
            : 'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md'
        )}
        title={isListening ? 'Detener' : 'Buscar por voz'}
      >
        <AnimatePresence mode="wait">
          {isListening ? (
            <motion.div
              key="listening"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ duration: 0.2 }}
            >
              <MicOff className="h-5 w-5" />
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ scale: 0, rotate: 180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: -180 }}
              transition={{ duration: 0.2 }}
            >
              <Mic className="h-5 w-5" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Onda de sonido animada */}
        {isListening && (
          <>
            <span className="absolute inset-0 rounded-lg bg-red-500 opacity-75 animate-ping" />
            <span className="absolute inset-0 rounded-lg bg-red-500 opacity-50 animate-pulse" />
          </>
        )}
      </button>

      {/* Transcripción en tiempo real */}
      <AnimatePresence>
        {(isListening || interimTranscript || transcript) && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute top-full left-0 right-0 mt-2 z-50"
          >
            <div className="bg-card border border-border rounded-lg shadow-lg p-4 min-w-[300px]">
              {/* Header */}
              <div className="flex items-center gap-2 mb-2">
                {isListening ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin text-red-500" />
                    <span className="text-xs font-medium text-red-500">Escuchando...</span>
                  </>
                ) : (
                  <>
                    <Mic className="h-4 w-4 text-green-500" />
                    <span className="text-xs font-medium text-green-500">Transcripción completada</span>
                  </>
                )}
              </div>

              {/* Transcripción */}
              <div className="text-sm">
                {transcript && (
                  <p className="text-foreground font-medium">{transcript}</p>
                )}
                {interimTranscript && (
                  <p className="text-muted-foreground italic">{interimTranscript}</p>
                )}
                {!transcript && !interimTranscript && isListening && (
                  <p className="text-muted-foreground italic">{placeholder}</p>
                )}
              </div>

              {/* Indicador de nivel de audio (decorativo) */}
              {isListening && (
                <div className="flex items-center gap-1 mt-3">
                  {[...Array(5)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-1 bg-red-500 rounded-full"
                      animate={{
                        height: [4, 16, 4],
                      }}
                      transition={{
                        duration: 0.6,
                        repeat: Infinity,
                        delay: i * 0.1,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
